import { observer } from "mobx-react-lite";

import { DateTime } from "@bps/date-time";
import { useAsync } from "@bps/hooks";
import {
  CalendarEventStatus,
  CalendarEventStatusText
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { FilterBarProps } from "@ui-components/filter-bar/FilterBar.types.ts";
import { showUnlessPrintingStyles } from "@ui-components/printing/Print.styles.ts";

import { durationOptions } from "../utils.ts";
import { AppointmentInformationPivotTabs } from "./AppointmentInformationPivotTabs.tsx";
import { AppointmentInformationModalInnerProps } from "./AppointmentInformationPivotTabs.types.ts";

export type ApptinfoFilterType = {
  claim?: string;
  apptStartDate?: Date;
  apptEndDate?: Date;
  status?: string[];
  apptType?: string[];
  providerIds?: string[];
  durationRange?: string;
};

const AppointmentInformationModalInnerBase: React.FC<AppointmentInformationModalInnerProps> =
  observer(props => {
    const { booking, core } = useStores();

    const appointmentStatusOptions =
      booking.ref.appointmentStatuses.keyTextValues;

    const statusOptions = appointmentStatusOptions.concat([
      {
        key: CalendarEventStatus.Cancelled,
        text: CalendarEventStatusText.CANCELLED
      }
    ]);

    const { data: apptTypes = [] } = useAsync(async () => {
      return await booking.loadAppointmentTypes();
    });

    const nameOf = nameOfFactory<ApptinfoFilterType>();
    const renderFilterItems: FilterBarProps["items"] = [];

    if (!props.claimId && core.isNZTenant) {
      renderFilterItems.push({
        name: nameOf("claim"),
        type: "searchBox",
        stickItem: true,
        props: {
          placeholder: "Search claim no"
        }
      });
    }

    renderFilterItems.push({
      name: nameOf("apptType"),
      type: "optionsSelect",
      props: {
        options: apptTypes.map(data => ({
          key: data.id,
          text: data.name
        })),
        placeholder: "Appointment type",
        styles: { root: { width: 300 } }
      }
    });

    renderFilterItems.push(
      {
        type: "datesRangePicker",
        name: "apptDatesRangePicker",
        props: {
          id: "appointment-information-filter-dates-range",
          startDateProps: { id: "appointment-information-filter-start-date" },
          endDateProps: { id: "appointment-information-filter-end-date" },
          startDateName: nameOf("apptStartDate"),
          endDateName: nameOf("apptEndDate"),
          placeholder: "Date(s)",
          renderValue: (start, end) => {
            if (!start || !end) {
              return "";
            }

            const _startDate = DateTime.fromJSDate(start).toDayDefaultFormat();
            const _endDate = DateTime.fromJSDate(end).toDayDefaultFormat();

            return _startDate !== _endDate
              ? `${_startDate} - ${_endDate}`
              : _startDate;
          }
        }
      },
      {
        type: "optionsSelect",
        name: nameOf("status"),
        props: {
          placeholder: "Status",
          label: "Status",
          options: statusOptions,
          hideSearchOption: true,
          calloutWidth: 130,
          multiSelect: true
        }
      },
      {
        type: "optionsSelect",
        name: nameOf("durationRange"),
        props: {
          placeholder: "Duration",
          label: "Duration",
          options: durationOptions,
          hideSearchOption: true,
          calloutWidth: 150,
          multiSelect: false
        }
      },
      {
        type: "bookableUsersSelect",
        name: nameOf("providerIds"),
        props: {
          id: "appointment-information-filter-providers",
          multiSelect: true,
          usersFilter: { showOnCalendar: true },
          placeholder: "Providers",
          calloutWidth: 250
        }
      }
    );

    return (
      <FilterBar<ApptinfoFilterType>
        items={renderFilterItems}
        styles={showUnlessPrintingStyles}
      >
        {() => <AppointmentInformationPivotTabs {...props} />}
      </FilterBar>
    );
  });

export const AppointmentInformationModalInner = withFetch(
  root =>
    root.core.isNZTenant
      ? root.practice.ref.accProviderContractTypes.load()
      : Promise.resolve(),
  AppointmentInformationModalInnerBase
);
